// The zone directory — "ZoneKey -> what the zone is now" (option A, phase 1;
// docs/reference/zone_and_settings_changes.md §4). Anything that SHOWS a zone name
// takes it from here; from phase 3 on, screens look zones up by key and use this for
// the label. Backend: GET /zone_directory/ (zone_identity.build_zone_directory).
//
// Entry: { zoneKey, zoneId, flowId, name, typeId, typeName, active, geometry,
//          names: [every name it has had], types: [every type it has been] }
import { defineStore } from 'pinia'

import { apiClientDB } from '@/config_sessions'

const norm = (s) => String(s ?? '').trim().toLowerCase()

export const useZoneDirectoryStore = defineStore('zoneDirectory', {
  state: () => ({
    zones: [],
    companyId: null,     // the company `zones` belongs to
    loading: false,
    error: null,
  }),

  getters: {
    byKey: (state) => new Map(state.zones.map((z) => [z.zoneKey, z])),
    activeZones: (state) => state.zones.filter((z) => z.active),
    // The zone's current label; null when the key is unknown.
    nameOf() {
      return (key) => this.byKey.get(key)?.name ?? null
    },
    // The key of the zone on `flowId` called `name` — now, or under an earlier name
    // (what name-keyed data still says until phase 5). A live zone wins over a deleted
    // one that once had the same name.
    keyOf: (state) => (flowId, name) => {
      const n = norm(name)
      const hits = state.zones.filter((z) => z.flowId === flowId
        && (norm(z.name) === n || (z.names || []).some((x) => norm(x) === n)))
      const pick = hits.find((z) => z.active && norm(z.name) === n)
        || hits.find((z) => z.active) || hits[0]
      return pick ? pick.zoneKey : null
    },
  },

  actions: {
    async fetch(companyId = null) {
      this.loading = true
      try {
        const r = await apiClientDB.get('/zone_directory/', {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        })
        this.zones = (r.data && r.data.zones) || []
        this.companyId = companyId
        this.error = null
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
      }
      return this.zones
    },

    // Load once per company; call fetch() to force a refresh (e.g. after a zone save).
    async ensureLoaded(companyId) {
      if (this.companyId === companyId && this.zones.length) return this.zones
      return this.fetch(companyId)
    },

    clear() {
      this.zones = []
      this.companyId = null
    },
  },
})
